/**
 * Google Analytics 4 utility functions
 * Provides tracking for page views, custom events, viewport sizes, and performance metrics
 */

// Type declaration for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Check if GA is available
 */
const isGAAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view (for SPA route changes)
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isGAAvailable()) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, any>,
): void => {
  if (!isGAAvailable()) return;

  window.gtag('event', eventName, params);
};

/**
 * Get MUI breakpoint category from width
 */
const getBreakpoint = (width: number): string => {
  if (width < 600) return 'xs';
  if (width < 900) return 'sm';
  if (width < 1200) return 'md';
  if (width < 1536) return 'lg';
  return 'xl';
};

/**
 * Get viewport and device information
 */
export const getViewportInfo = () => ({
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  devicePixelRatio: window.devicePixelRatio,
  breakpoint: getBreakpoint(window.innerWidth),
  // Connection info if available
  connectionType: (navigator as any).connection?.effectiveType || 'unknown',
  connectionDownlink: (navigator as any).connection?.downlink || 'unknown',
});

/**
 * Set user properties for segmentation in GA4
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!isGAAvailable()) return;

  window.gtag('set', 'user_properties', properties);
};

/**
 * Initialize viewport tracking - call once on app load
 * Sets viewport dimensions as user properties for segmentation
 */
export const initViewportTracking = (): void => {
  if (!isGAAvailable()) return;

  const info = getViewportInfo();

  // Set as user properties so every event includes this context
  setUserProperties({
    viewport_width: info.viewportWidth,
    viewport_height: info.viewportHeight,
    viewport_breakpoint: info.breakpoint,
    device_pixel_ratio: info.devicePixelRatio,
    connection_type: info.connectionType,
  });

  // Also send as an event for historical tracking
  trackEvent('viewport_info', {
    viewport_width: info.viewportWidth,
    viewport_height: info.viewportHeight,
    viewport_breakpoint: info.breakpoint,
    screen_width: info.screenWidth,
    screen_height: info.screenHeight,
    device_pixel_ratio: info.devicePixelRatio,
    connection_type: info.connectionType,
    connection_downlink: info.connectionDownlink,
  });

  // Track viewport changes (resize, orientation change)
  // Debounce timeout to avoid spam on rapid resizes
  let resizeTimeout: NodeJS.Timeout | null = null;
  window.addEventListener('resize', () => {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const newInfo = getViewportInfo();
      trackEvent('viewport_resize', {
        viewport_width: newInfo.viewportWidth,
        viewport_height: newInfo.viewportHeight,
        viewport_breakpoint: newInfo.breakpoint,
      });
    }, 1000);
  });
};

/**
 * Track Web Vitals metrics
 * Pass this to reportWebVitals()
 */
export const trackWebVitals = (metric: {
  name: string;
  value: number;
  id: string;
}): void => {
  if (!isGAAvailable()) return;

  // GA4 event names must be lowercase with underscores
  const eventName = `web_vitals_${metric.name.toLowerCase()}`;

  trackEvent(eventName, {
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    metric_id: metric.id,
    metric_name: metric.name,
    // Include viewport context with performance metrics
    ...getViewportInfo(),
  });
};

// Dashboard-specific tracking events

/**
 * Track search execution
 */
export const trackSearch = (params: {
  resultCount: number;
  filterCount: number;
  hasDateFilter: boolean;
  hasMakeFilter: boolean;
  hasPlateFilter: boolean;
  searchDurationMs?: number;
}): void => {
  trackEvent('search_executed', {
    result_count: params.resultCount,
    filter_count: params.filterCount,
    has_date_filter: params.hasDateFilter,
    has_make_filter: params.hasMakeFilter,
    has_plate_filter: params.hasPlateFilter,
    search_duration_ms: params.searchDurationMs,
    ...getViewportInfo(),
  });
};

/**
 * Track tab changes
 */
export const trackTabChange = (tabName: string, tabIndex: number): void => {
  trackEvent('tab_change', {
    tab_name: tabName,
    tab_index: tabIndex,
  });
};

/**
 * Track export actions
 */
export const trackExport = (params: {
  exportType: 'reads' | 'alerts';
  itemCount: number;
  format?: string;
}): void => {
  trackEvent('export', {
    export_type: params.exportType,
    item_count: params.itemCount,
    format: params.format || 'default',
  });
};

/**
 * Track infinite scroll load more
 */
export const trackLoadMore = (params: {
  pageNumber: number;
  totalLoaded: number;
  loadTimeMs?: number;
}): void => {
  trackEvent('load_more', {
    page_number: params.pageNumber,
    total_loaded: params.totalLoaded,
    load_time_ms: params.loadTimeMs,
  });
};

/**
 * Track view mode changes (grid vs image view)
 */
export const trackViewModeChange = (viewMode: 'default' | 'image'): void => {
  trackEvent('view_mode_change', {
    view_mode: viewMode,
  });
};
